import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Lightning, Trash, Play } from '@phosphor-icons/react';

const TONE_STYLE = { gentle: 'bg-sky-100 text-sky-800', firm: 'bg-amber-100 text-amber-800', final: 'bg-rose-100 text-rose-700' };

function WorkflowBuilder({ meta, templates, onDone }) {
  const [f, setF] = useState({ name: '', trigger: 'student_admitted', cField: '', cOp: 'eq', cValue: '', aType: 'notify_roles', aRoles: 'school_admin', aMessage: '', aTemplate: '', aUrl: '', aAmount: '' });
  const createMut = useMutation({
    mutationFn: () => {
      const action = f.aType === 'notify_roles' ? { type: 'notify_roles', roles: f.aRoles.split(',').map((r) => r.trim()).filter(Boolean), message: f.aMessage }
        : f.aType === 'send_template' ? { type: 'send_template', template_id: f.aTemplate, to: 'guardians' }
        : f.aType === 'webhook' ? { type: 'webhook', url: f.aUrl }
        : { type: 'add_late_fee', amount: parseFloat(f.aAmount) || 0 };
      const conditions = f.cField && f.cValue ? [{ field: f.cField, op: f.cOp, value: f.cValue }] : [];
      return api.post('/workflows', { name: f.name, trigger: f.trigger, conditions, actions: [action] });
    },
    onSuccess: () => { toast.success('Workflow created'); setF({ ...f, name: '', cField: '', cValue: '', aMessage: '' }); onDone(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const trig = (meta?.triggers || []).find((t) => t.key === f.trigger);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="workflow-builder">
      <p className="font-display font-bold">New workflow</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input data-testid="wf-name-input" placeholder="Name (e.g. Welcome new students)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <select data-testid="wf-trigger-select" value={f.trigger} onChange={(e) => setF({ ...f, trigger: e.target.value, cField: '' })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
          {(meta?.triggers || []).map((t) => <option key={t.key} value={t.key}>When: {t.label}</option>)}
        </select>
      </div>
      <p className="font-mono-label text-[10px] uppercase text-slate-400">Condition (optional) - fields: {trig?.fields?.join(', ')}</p>
      <div className="grid grid-cols-3 gap-2">
        <select data-testid="wf-cond-field" value={f.cField} onChange={(e) => setF({ ...f, cField: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
          <option value="">No condition - always run</option>
          {(trig?.fields || []).map((fl) => <option key={fl} value={fl}>{fl}</option>)}
        </select>
        <select data-testid="wf-cond-op" value={f.cOp} onChange={(e) => setF({ ...f, cOp: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
          {(meta?.ops || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <Input data-testid="wf-cond-value" placeholder="Value" value={f.cValue} onChange={(e) => setF({ ...f, cValue: e.target.value })} />
      </div>
      <p className="font-mono-label text-[10px] uppercase text-slate-400">Action - placeholders like {'{{'}{trig?.fields?.[0] || 'name'}{'}}'} work in messages</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <select data-testid="wf-action-type" value={f.aType} onChange={(e) => setF({ ...f, aType: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
          <option value="notify_roles">Notify staff roles</option>
          <option value="send_template">Send template to guardians</option>
          <option value="webhook">Call a webhook (https)</option>
          {f.trigger === 'invoice_overdue' && <option value="add_late_fee">Add late fee to invoice</option>}
        </select>
        {f.aType === 'notify_roles' && <Input data-testid="wf-action-roles" placeholder="Roles (comma separated: school_admin, principal)" value={f.aRoles} onChange={(e) => setF({ ...f, aRoles: e.target.value })} />}
        {f.aType === 'send_template' && (
          <select data-testid="wf-action-template" value={f.aTemplate} onChange={(e) => setF({ ...f, aTemplate: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
            <option value="">Pick a template...</option>
            {(templates || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        {f.aType === 'webhook' && <Input data-testid="wf-action-url" placeholder="https://hooks.zapier.com/..." value={f.aUrl} onChange={(e) => setF({ ...f, aUrl: e.target.value })} />}
        {f.aType === 'add_late_fee' && <Input data-testid="wf-action-amount" type="number" placeholder="Late fee amount" value={f.aAmount} onChange={(e) => setF({ ...f, aAmount: e.target.value })} />}
      </div>
      {f.aType === 'notify_roles' && <Input data-testid="wf-action-message" placeholder="Message - e.g. New student {{name}} admitted to grade {{grade}}" value={f.aMessage} onChange={(e) => setF({ ...f, aMessage: e.target.value })} />}
      <Button data-testid="wf-create-btn" className="bg-slate-900 hover:bg-slate-800" disabled={!f.name.trim() || createMut.isPending} onClick={() => createMut.mutate()}>Create workflow</Button>
    </div>
  );
}

function Dunning() {
  const qc = useQueryClient();
  const { data: s } = useQuery({ queryKey: ['dunning'], queryFn: () => api.get('/workflows/dunning/settings').then((r) => r.data) });
  const { data: log } = useQuery({ queryKey: ['dunning-log'], queryFn: () => api.get('/workflows/dunning/log').then((r) => r.data) });
  const [draft, setDraft] = useState(null);
  const cur = draft || s;
  const saveMut = useMutation({
    mutationFn: () => api.put('/workflows/dunning/settings', { enabled: cur.enabled, stages: cur.stages.map((x) => ({ ...x, days: parseInt(x.days, 10) || 1, late_fee: parseFloat(x.late_fee) || 0, subject: x.subject || null, body: x.body || null })) }),
    onSuccess: () => { toast.success('Dunning settings saved'); setDraft(null); qc.invalidateQueries({ queryKey: ['dunning'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!cur) return null;
  const setStage = (i, patch) => setDraft({ ...cur, stages: cur.stages.map((x, j) => j === i ? { ...x, ...patch } : x) });
  return (
    <div className="space-y-4" data-testid="dunning-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="font-display font-bold">Escalating overdue reminders</p>
            <p className="text-xs text-slate-500">Runs automatically every day - gentle - firm - final notice, with an optional late fee at any stage.</p>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="dunning-enabled" checked={cur.enabled} onCheckedChange={(v) => setDraft({ ...cur, enabled: v })} /> {cur.enabled ? 'Enabled' : 'Disabled'}</label>
        </div>
        <div className="mt-4 space-y-2">
          {cur.stages.map((st, i) => (
            <div key={st.tone} className="border border-slate-100 rounded-lg p-3" data-testid={`dunning-stage-${st.tone}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`px-2 py-0.5 rounded text-xs uppercase font-semibold ${TONE_STYLE[st.tone]}`}>{st.tone}</span>
                <span>after</span>
                <Input data-testid={`dunning-days-${st.tone}`} className="w-20" type="number" value={st.days} onChange={(e) => setStage(i, { days: e.target.value })} />
                <span>days overdue · late fee</span>
                <Input data-testid={`dunning-fee-${st.tone}`} className="w-28" type="number" value={st.late_fee} onChange={(e) => setStage(i, { late_fee: e.target.value })} />
                <span className="text-xs text-slate-400">{Number(st.late_fee) > 0 ? 'fee auto-added when this notice fires' : 'no fee'}</span>
              </div>
              <div className="grid sm:grid-cols-[1fr_2fr] gap-2 mt-2">
                <Input data-testid={`dunning-subject-${st.tone}`} placeholder="Custom subject (optional) - use {student} {plan} {balance} {days}" value={st.subject || ''} onChange={(e) => setStage(i, { subject: e.target.value })} />
                <Input data-testid={`dunning-body-${st.tone}`} placeholder="Custom message (optional) - leave empty for the default wording" value={st.body || ''} onChange={(e) => setStage(i, { body: e.target.value })} />
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="dunning-save" size="sm" className="mt-3 bg-slate-900 hover:bg-slate-800" disabled={!draft || saveMut.isPending} onClick={() => saveMut.mutate()}>Save settings</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="dunning-log">
        <p className="font-display font-bold mb-2">Notice log</p>
        {(log || []).length === 0 && <p className="text-sm text-slate-400">No dunning notices sent yet - the daily run only sends when an invoice crosses a stage.</p>}
        {(log || []).map((l) => (
          <p key={l.id} className="text-xs text-slate-600 border-b border-slate-50 py-1.5">
            <span className={`px-1.5 rounded uppercase ${TONE_STYLE[l.tone]}`}>{l.tone}</span> {l.at?.slice(0, 16)} · <b>{l.student_name}</b> · {l.plan_name} · ₹{Number(l.balance).toLocaleString()} · {l.days_overdue}d overdue · {l.guardians_notified} guardian(s){l.late_fee_applied > 0 && <span className="text-rose-600"> · late fee ₹{l.late_fee_applied}</span>}
          </p>
        ))}
      </div>
    </div>
  );
}

export default function Automation() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('workflows');
  const [tf, setTf] = useState({ name: '', subject: '', body: '' });
  const { data: meta } = useQuery({ queryKey: ['wf-meta'], queryFn: () => api.get('/workflows/triggers').then((r) => r.data) });
  const { data: wfs } = useQuery({ queryKey: ['wfs'], queryFn: () => api.get('/workflows').then((r) => r.data) });
  const { data: templates } = useQuery({ queryKey: ['wf-templates'], queryFn: () => api.get('/workflows/templates').then((r) => r.data) });
  const { data: runs } = useQuery({ queryKey: ['wf-runs'], queryFn: () => api.get('/workflows/runs').then((r) => r.data), enabled: tab === 'runs' });
  const inval = (k) => qc.invalidateQueries({ queryKey: [k] });
  const toggleMut = useMutation({ mutationFn: (id) => api.patch(`/workflows/${id}/toggle`), onSuccess: () => inval('wfs'), onError: (e) => toast.error(errMsg(e)) });
  const delMut = useMutation({ mutationFn: (id) => api.delete(`/workflows/${id}`), onSuccess: () => { toast.success('Deleted'); inval('wfs'); }, onError: (e) => toast.error(errMsg(e)) });
  const tplMut = useMutation({
    mutationFn: () => api.post('/workflows/templates', tf),
    onSuccess: () => { toast.success('Template saved'); setTf({ name: '', subject: '', body: '' }); inval('wf-templates'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const tplDelMut = useMutation({ mutationFn: (id) => api.delete(`/workflows/templates/${id}`), onSuccess: () => inval('wf-templates'), onError: (e) => toast.error(errMsg(e)) });
  const testRun = async (id) => {
    try {
      const { data } = await api.post(`/workflows/${id}/run-test`);
      toast[data.conditions_passed ? 'success' : 'warning'](data.conditions_passed
        ? data.planned_actions.map((a) => a.would).join(' · ')
        : 'Conditions did NOT match the sample entity - workflow would be skipped');
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5" data-testid="automation-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><Lightning size={26} className="text-orange-600" />Automation</h1>
        <p className="text-sm text-slate-500">No-code workflows, reusable templates and escalating fee reminders.</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {[['workflows', 'Workflows'], ['templates', 'Templates'], ['runs', 'Run history'], ['dunning', 'Fee reminders (dunning)']].map(([k, l]) => (
          <button key={k} data-testid={`automation-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-sm ${tab === k ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>{l}</button>
        ))}
      </div>
      {tab === 'workflows' && (
        <div className="space-y-4">
          <WorkflowBuilder meta={meta} templates={templates} onDone={() => inval('wfs')} />
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="workflow-list">
            <p className="font-display font-bold mb-2">Your workflows</p>
            {(wfs || []).length === 0 && <p className="text-sm text-slate-400">No workflows yet - create one above.</p>}
            {(wfs || []).map((w) => (
              <div key={w.id} className="flex items-center justify-between gap-2 border-b border-slate-50 py-2 flex-wrap" data-testid={`workflow-row-${w.id}`}>
                <div className="text-sm">
                  <b>{w.name}</b> <span className="text-xs text-slate-400">when {w.trigger.replace(/_/g, ' ')}</span>
                  <p className="text-[11px] text-slate-400">{w.conditions.length ? `if ${w.conditions.map((c) => `${c.field} ${c.op} ${c.value}`).join(' & ')} - ` : ''}{w.actions.map((a) => a.type.replace(/_/g, ' ')).join(', ')} · {w.runs || 0} run(s)</p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`wf-test-${w.id}`} onClick={() => testRun(w.id)} className="text-xs border border-slate-200 rounded px-2 py-1 flex items-center gap-1 hover:bg-slate-50"><Play size={12} />Test</button>
                  <Switch data-testid={`wf-toggle-${w.id}`} checked={w.enabled} onCheckedChange={() => toggleMut.mutate(w.id)} />
                  <button data-testid={`wf-delete-${w.id}`} onClick={() => delMut.mutate(w.id)} className="text-rose-500"><Trash size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === 'templates' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2" data-testid="template-form">
            <p className="font-display font-bold">New template</p>
            <p className="text-xs text-slate-500">Placeholders: {'{{student_name}} {{name}} {{plan_name}} {{balance}} {{grade}} {{days_overdue}}'}</p>
            <Input data-testid="tpl-name" placeholder="Template name" value={tf.name} onChange={(e) => setTf({ ...tf, name: e.target.value })} />
            <Input data-testid="tpl-subject" placeholder="Subject - e.g. Welcome {{name}}!" value={tf.subject} onChange={(e) => setTf({ ...tf, subject: e.target.value })} />
            <textarea data-testid="tpl-body" rows={5} placeholder="Body..." value={tf.body} onChange={(e) => setTf({ ...tf, body: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <Button data-testid="tpl-save" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={tplMut.isPending} onClick={() => tplMut.mutate()}>Save template</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="template-list">
            <p className="font-display font-bold mb-2">Templates</p>
            {(templates || []).length === 0 && <p className="text-sm text-slate-400">No templates yet.</p>}
            {(templates || []).map((t) => (
              <div key={t.id} className="border-b border-slate-50 py-2 flex items-start justify-between gap-2">
                <div className="text-sm"><b>{t.name}</b><p className="text-xs text-slate-500">{t.subject}</p><p className="text-[11px] text-slate-400 line-clamp-2">{t.body}</p></div>
                <button data-testid={`tpl-delete-${t.id}`} onClick={() => tplDelMut.mutate(t.id)} className="text-rose-500"><Trash size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === 'runs' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="run-history">
          <p className="font-display font-bold mb-2">Run history</p>
          {(runs || []).length === 0 && <p className="text-sm text-slate-400">No runs yet - workflows fire on real events (admission, payment, overdue).</p>}
          {(runs || []).map((r) => (
            <div key={r.id} className="border-b border-slate-50 py-2 text-xs text-slate-600">
              <b>{r.workflow_name || r.trigger}</b> · {r.at?.slice(0, 16)} · {(r.results || []).map((x) => `${x.type}: ${x.result}`).join(' · ')}
            </div>
          ))}
        </div>
      )}
      {tab === 'dunning' && <Dunning />}
    </div>
  );
}
